import React, { useEffect, useRef, useState } from 'react';
import { bool } from 'prop-types';
import { I18n } from '@fiverr-private/i18n-react';
import { Popover, PopoverAnchor } from '@fiverr-private/popover';
import { useGigPageContext } from '@fiverr-private/gig_page_context';
import { ChevronDownIcon, ChevronUpIcon } from '@fiverr-private/icons';
import { Container } from '@fiverr-private/layout_components';
import { Typography } from '@fiverr-private/typography';
import { reportContactSellerOptionsClick } from '../../../utils/contactSeller';
import { fibMigrationFlow, FibTriggers } from '../../../utils/fibMigrationFlow';
import { trackContactMeClickEvent } from '../../../utils/gtmTracking/gtmTracking';
import { peekContactTrigger } from '../../../utils/contactAutoTrigger/contactAutoTrigger';
import { CONTACT_TRIGGER_VALUES } from '../../../reducers/packages/utils/constants';
import PopoverContent from './PopoverContent';
import { ButtonWithOptions } from './ButtonWithOptions';

const DROPDOWN_AUTO_TRIGGERS = new Set([
    CONTACT_TRIGGER_VALUES.MESSAGE,
    CONTACT_TRIGGER_VALUES.CUSTOM_ORDER,
]);

const ContactSellerOptions = ({ customOrderEligible, hidden = false }) => {
    const { biEvents, seller, isInCheetahsGigPageButtonsColors } = useGigPageContext();
    const contactSellerButtonRef = useRef();
    const [isOpen, setIsOpen] = useState(false);

    const { isAgency } = seller;
    const contentColor = isInCheetahsGigPageButtonsColors ? 'white' : 'grey_1100';
    const ctaTitle = isAgency
        ? `gig_page_perseus.contact_seller.title_agency`
        : `gig_page_perseus.contact_seller.title`;

    useEffect(() => {
        handlePersistedFibMigrationTriggers(contactSellerButtonRef.current);
        // The popover's content unmounts when closed, so its children's
        // consumeContactTrigger useEffects never fire on a post-sign-in
        // reload. Open the dropdown so they mount and pick up the trigger.
        if (DROPDOWN_AUTO_TRIGGERS.has(peekContactTrigger())) {
            setIsOpen(true);
        }
    }, [contactSellerButtonRef]);

    const onOpen = async () => {
        setIsOpen(true);
        reportContactSellerOptionsClick(biEvents);
        trackContactMeClickEvent();
    };

    const onClose = () => {
        setIsOpen(false);
    };

    useEffect(() => {
        if (hidden) {
            onClose();
        }
    }, [hidden]);

    const onOpenChanged = (isOpen) => isOpen ? onOpen() : onClose();

    const handlePersistedFibMigrationTriggers = (buttonEl) => {
        if (!buttonEl) {
            return;
        }
        const isAskQuestionTriggerPersisted = fibMigrationFlow.isTriggerIdPersisted(FibTriggers.Contact);
        const isGetQuoteTriggerPersisted = fibMigrationFlow.isTriggerIdPersisted(FibTriggers.GetQuote);

        if (isAskQuestionTriggerPersisted || isGetQuoteTriggerPersisted) {
            // README: We have to open dropdown for run logic inside options
            buttonEl.dispatchEvent(new Event('click', { bubbles: true }));
        }
    };

    return (
        <div className="contact-seller-options-wrapper">
            <Popover appendToBody={false} isOpen={isOpen} onOpenChanged={onOpenChanged} >
                <PopoverAnchor fullWidth>
                    <ButtonWithOptions ref={contactSellerButtonRef} isDark={isInCheetahsGigPageButtonsColors}>
                        <Typography color={contentColor}>
                            <I18n k={ctaTitle} />
                        </Typography>
                        <Container marginLeft="2">
                            {isOpen ? (
                                <ChevronUpIcon size="10" color={contentColor} fill={{ hover: 'white' }} />
                            ) : (
                                <ChevronDownIcon size="10" color={contentColor} fill={{ hover: 'white' }} />
                            )}
                        </Container>
                    </ButtonWithOptions>
                </PopoverAnchor>
                <PopoverContent customOrderEligible={customOrderEligible} />
            </Popover>
        </div>
    );
};

ContactSellerOptions.propTypes = {
    customOrderEligible: bool,
    hidden: bool,
};

export default ContactSellerOptions;